import { Modal, Button, message } from "antd";
import { useRef, useState } from "react";
import { API_URL } from "../../env";

interface UploadModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess?: () => void | Promise<void>;   // ← Đã thêm
}

const VALID_TYPES = [
    "text/csv",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];

const ACCEPTED_EXTENSIONS = ".csv,.xls,.xlsx";

export default function UploadModal({ 
    open, 
    onClose, 
    onSuccess 
}: UploadModalProps) {
    
    const [file, setFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const resetState = () => {
        setFile(null);
        if (inputRef.current) inputRef.current.value = "";
    };

    const handleClose = () => {
        resetState();
        onClose();
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selected = e.target.files?.[0];
        if (!selected) return;

        if (!VALID_TYPES.includes(selected.type)) {
            message.error("Chỉ chấp nhận file CSV, XLS, XLSX");
            if (inputRef.current) inputRef.current.value = "";
            return;
        }

        setFile(selected);
    };

    const handleUpload = async () => {
        if (!file) {
            message.warning("Vui lòng chọn file");
            return;
        }

        const token = localStorage.getItem("token");

        setLoading(true);
        try {
            const formData = new FormData();
            formData.append("file", file);

            const response = await fetch(`${API_URL}/api/v1/be/orders/import`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                body: formData,
            });

            if (!response.ok) {
                const error = await response.json().catch(() => ({}));
                message.error(error?.message || "Tải lên thất bại");
                return;
            }

            await response.json();
            message.success("Tải lên thành công!");

            // Gọi callback để refresh danh sách ở trang cha
            if (onSuccess) {
                await onSuccess();   // Hỗ trợ cả async function
            }

            handleClose();
            
        } catch (err) {
            console.error(err);
            message.error("Lỗi kết nối server");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            open={open}
            onCancel={handleClose}
            footer={null}
            centered
            width={600}
            maskClosable={!loading}
            closable={!loading}
        >
            <div className="space-y-4">

                {/* TITLE */}
                <h2 className="text-xl font-semibold text-green-600 border-b pb-2">
                    Nạp danh sách
                </h2>

                {/* DESCRIPTION */}
                <p className="text-sm text-gray-600">
                    Chọn file Excel import (chỉ chấp nhận file .csv, .xls, .xlsx —{" "}
                    <span className="text-blue-500 cursor-pointer hover:underline">
                        tải mẫu file tại đây
                    </span>
                    )
                </p>

                {/* FILE INPUT */}
                <input
                    ref={inputRef}
                    type="file"
                    accept={ACCEPTED_EXTENSIONS}
                    onChange={handleFileChange}
                    disabled={loading}
                    className="border rounded px-3 py-2 w-full text-sm 
                               file:mr-3 file:py-1 file:px-3 file:rounded 
                               file:border-0 file:text-sm 
                               file:bg-green-50 file:text-green-700 
                               hover:file:bg-green-100 disabled:opacity-50"
                />

                {/* SELECTED FILE NAME */}
                {file && (
                    <p className="text-sm text-green-600">
                        Đã chọn: <span className="font-medium">{file.name}</span>
                    </p>
                )}

                {/* NOTE */}
                <p className="text-xs text-gray-400 italic">
                    Lưu ý: chọn file định dạng .csv nếu import từ Macbook
                </p>

                {/* FOOTER */}
                <div className="flex justify-end gap-2 pt-4 border-t">
                    <Button onClick={handleClose} disabled={loading}>
                        Hủy
                    </Button>
                    <Button
                        type="primary"
                        className="bg-green-500"
                        onClick={handleUpload}
                        loading={loading}
                    >
                        Tải lên
                    </Button>
                </div>
            </div>
        </Modal>
    );
}